// Parsing for campaignTemplates_list results, kept out of the component so the message list can
// merge several searches in one turn into a single picker.

export interface TemplateCard {
  templateId: number;
  name: string;
  previewImageUrl?: string;
  isSurvey: boolean;
  meta?: string;
}

// Rows missing a numeric id or a name are dropped.
export function parseTemplateCards(content: unknown): TemplateCard[] {
  const cards: TemplateCard[] = [];
  for (const item of extractItems(content)) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const row = item as Record<string, unknown>;
    const rawId = row['templateId'];
    const rawName = row['name'];
    if (typeof rawId !== 'number' || typeof rawName !== 'string' || rawName.length === 0) {
      continue;
    }
    cards.push({
      templateId: rawId,
      name: rawName,
      previewImageUrl: firstString(row['previewImageUrls']),
      isSurvey: row['isSurvey'] === true,
      meta:
        withoutLeadingQuestion(firstString(row['descriptionItems'])) ??
        firstString(row['objectives']) ??
        firstString(row['tactics']) ??
        firstString(row['layouts']),
    });
  }
  return cards;
}

// Union of several results, deduped by templateId (first occurrence wins).
export function mergeTemplateCards(contents: unknown[]): TemplateCard[] {
  const byId = new Map<number, TemplateCard>();
  for (const content of contents) {
    for (const card of parseTemplateCards(content)) {
      if (!byId.has(card.templateId)) {
        byId.set(card.templateId, card);
      }
    }
  }
  return [...byId.values()];
}

// The tool returns { totalMatching, items: [...] }; tolerate a bare array too.
function extractItems(content: unknown): unknown[] {
  if (Array.isArray(content)) {
    return content;
  }
  if (content && typeof content === 'object') {
    const items = (content as { items?: unknown }).items;
    if (Array.isArray(items)) {
      return items;
    }
  }
  return [];
}

// Most catalog descriptions open with the heading "What is this template used for?".
function withoutLeadingQuestion(description: string | undefined): string | undefined {
  return description?.replace(/^[^?]{1,60}\?\s+(?=\S)/, '');
}

function firstString(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    const first = value.find((entry) => typeof entry === 'string' && entry.length > 0);
    return typeof first === 'string' ? first : undefined;
  }
  return undefined;
}
